import { useEffect, useState } from "react";
import type { MemberRemark } from "@/services/types";
import { groupService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { Alert } from "@/components/base/Alert";
import { Tooltip } from "@/components/base/Tooltip";
import { formatDate } from "@/utils/format";
import {
  MAX_REMARKS,
  MAX_REMARK_LENGTH,
  REMARK_RETENTION_DAYS,
  addDays,
  daysUntilDeleted,
  followUpTone,
  type FollowUpTone,
  type RemarksTarget,
} from "./remarkUtils";

const TONE_CLASS: Record<FollowUpTone, string> = {
  overdue: "bg-danger-100 text-danger-700",
  today: "bg-amber-100 text-amber-800",
  upcoming: "bg-background-200 text-foreground-600",
};

const TONE_LABEL: Record<FollowUpTone, string> = {
  overdue: "Overdue",
  today: "Due today",
  upcoming: "Follow up",
};

export function FollowUpChip({ date }: { date: string }) {
  const tone = followUpTone(date);
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}>
      <i className="ri-calendar-event-line" />
      {TONE_LABEL[tone]} · {formatDate(date)}
    </span>
  );
}

interface RemarksCellProps {
  remarks: MemberRemark[];
  canEdit: boolean;
  onOpen: () => void;
}

/** Table cell: latest remark, the most urgent follow-up, and the count. Clicking opens the modal. */
export function RemarksCell({ remarks, canEdit, onOpen }: RemarksCellProps) {
  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };
  const latest = remarks[0];
  if (!latest) {
    return canEdit ? (
      <button type="button" onClick={open} className="cursor-pointer text-xs text-foreground-400 hover:text-primary-700 hover:underline">
        <i className="ri-sticky-note-add-line mr-1" />
        Add remark
      </button>
    ) : (
      <span className="text-foreground-400">—</span>
    );
  }
  // Surface the most urgent follow-up, not just the latest remark's.
  const nextFollowUp = remarks
    .map((r) => r.followUpDate)
    .filter((d): d is string => Boolean(d))
    .sort()[0];
  return (
    <button type="button" onClick={open} className="block max-w-[14rem] cursor-pointer text-left">
      <Tooltip side="top" content={<span className="block max-w-xs whitespace-pre-wrap">{latest.remark}</span>}>
        <span className="block truncate text-xs text-foreground-800 hover:text-primary-700">{latest.remark}</span>
      </Tooltip>
      <span className="mt-0.5 flex items-center gap-1.5">
        {nextFollowUp ? <FollowUpChip date={nextFollowUp} /> : null}
        <span className="tnum text-[11px] text-foreground-400">
          {remarks.length}/{MAX_REMARKS}
        </span>
      </span>
    </button>
  );
}

interface MemberRemarksModalProps {
  open: boolean;
  groupId: string;
  target: RemarksTarget | null;
  readOnly?: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function MemberRemarksModal({ open, groupId, target, readOnly = false, onClose, onChanged }: MemberRemarksModalProps) {
  const toast = useToast();
  const [remarks, setRemarks] = useState<MemberRemark[]>([]);
  const [text, setText] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(target?.remarks ?? []);
    setText("");
    setFollowUpDate("");
    setError(null);
  }, [open, target]);

  if (!target) return null;
  const { groupMemberId, memberName, ticketNo } = target;

  const handleAdd = async () => {
    const remark = text.trim();
    if (!remark) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await groupService.addMemberRemark(groupId, groupMemberId, {
        remark,
        followUpDate: followUpDate || undefined,
      });
      setRemarks(updated);
      setText("");
      setFollowUpDate("");
      toast.success("Remark added", `Saved for ${memberName}.`);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add remark.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (remarkId: string) => {
    setDeletingId(remarkId);
    setError(null);
    try {
      await groupService.deleteMemberRemark(groupId, groupMemberId, remarkId);
      setRemarks((prev) => prev.filter((r) => r.id !== remarkId));
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete remark.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={`Remarks — ${memberName} (Ticket #${ticketNo})`}
      description={`Up to ${MAX_REMARKS} remarks per member. Remarks are deleted automatically after ${REMARK_RETENTION_DAYS} days.`}
      footer={
        readOnly ? (
          <div className="flex w-full justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-3">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Close
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!text.trim() || submitting}
              icon={submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-add-line" />}
            >
              {submitting ? "Saving…" : "Add remark"}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="Something went wrong">
            {error}
          </Alert>
        ) : null}

        {remarks.length === 0 ? (
          <p className="rounded-lg border border-dashed border-background-300 px-3 py-4 text-center text-xs text-foreground-400">
            No remarks yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {remarks.map((r) => (
              <li key={r.id} className="rounded-lg border border-background-200 bg-background-50 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="whitespace-pre-wrap break-words text-sm text-foreground-900">{r.remark}</p>
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(r.id)}
                      disabled={deletingId === r.id}
                      title="Delete remark"
                      className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-danger-50 hover:text-danger-600 transition-colors disabled:opacity-50"
                    >
                      <i className={deletingId === r.id ? "ri-loader-4-line animate-spin text-sm" : "ri-delete-bin-line text-sm"} />
                    </button>
                  ) : null}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-400">
                  {r.followUpDate ? <FollowUpChip date={r.followUpDate} /> : null}
                  <span>
                    {r.createdByName ? `by ${r.createdByName} · ` : ""}
                    {formatDate(r.createdAt)} · deletes in {daysUntilDeleted(r.createdAt)} days
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!readOnly ? (
          <div className="space-y-3 border-t border-background-200 pt-4">
            <Textarea
              label="New remark"
              placeholder="e.g. Will pay after 3 days"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={MAX_REMARK_LENGTH}
              rows={2}
            />
            <div className="space-y-2">
              <DatePicker label="Follow-up date (optional)" value={followUpDate || null} onChange={setFollowUpDate} />
              <div className="flex flex-wrap items-center gap-1.5">
                {[1, 3, 7].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setFollowUpDate(addDays(days))}
                    className="cursor-pointer rounded border border-background-300 px-2 py-0.5 text-xs text-foreground-600 hover:border-primary-400 hover:text-primary-700 transition-colors"
                  >
                    +{days} day{days === 1 ? "" : "s"}
                  </button>
                ))}
                {followUpDate ? (
                  <button
                    type="button"
                    onClick={() => setFollowUpDate("")}
                    className="cursor-pointer px-1 text-xs text-foreground-400 hover:text-foreground-700"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
            {remarks.length >= MAX_REMARKS ? (
              <p className="text-xs text-amber-700">
                <i className="ri-information-line mr-1" />
                Adding a new remark will remove the oldest one.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
